const COOKIE_MAX_AGE = 60 * 60 * 24 * 365

// A boolean layout preference kept in two places on purpose: a cookie, so
// Rails can stamp the matching class onto <html> at render time and the layout
// is right on the first frame; and that class, which Turbo Drive never replaces
// and so survives every navigation on its own.
export class LayoutPreference {
  value = false

  constructor(
    private readonly className: string,
    private readonly cookieName: string
  ) {}

  // The class on <html> is authoritative. The cookie is only a fallback for
  // Turbo visits arriving from a layout that never had the class (kiosk, auth,
  // public), whose <html> survives into this page.
  restore(): boolean {
    this.value = document.documentElement.classList.contains(this.className) || this.readCookie()
    if (this.value) document.documentElement.classList.add(this.className)
    return this.value
  }

  // For a mode that dictates its own layout (editing the menu, which is always
  // full width). Turbo never replaces <html>, so the class rides in from the
  // previous page and has to be dropped here; the cookie is left alone so the
  // user's own choice is waiting for them on the way out.
  suspend() {
    this.value = false
    document.documentElement.classList.remove(this.className)
  }

  set(value: boolean) {
    this.value = value
    this.writeCookie(value)
    document.documentElement.classList.toggle(this.className, value)
  }

  private readCookie(): boolean {
    return document.cookie.split('; ').some((pair) => pair === `${this.cookieName}=1`)
  }

  // Always write a value rather than deleting: restore() falls back to the
  // cookie, and an absent one could not be told apart from "never set".
  // `secure` only over https — browsers drop secure cookies on
  // http://localhost, which would quietly break dev and the tests.
  private writeCookie(value: boolean) {
    const secure = window.location.protocol === 'https:' ? '; secure' : ''
    document.cookie =
      `${this.cookieName}=${value ? '1' : '0'}; path=/; ` +
      `max-age=${COOKIE_MAX_AGE}; samesite=lax${secure}`
  }
}
